import type { BuiltInTool } from './types'

/** Mail-specific approval tools: escalation is run-callable; the older reply-draft gate remains OS-owned. */
export const OS_ESCALATION_TOOLS: readonly BuiltInTool[] = [
    {
        name: 'os.escalate_mail',
        family: 'os.escalation',
        summary: 'The gate a mail that needs the CEO opens: the mail, why it matters, and what is suggested.',
        description:
            'Escalate a mail the CEO has to see (a VIP, something sensitive, or a question only they can answer), then stop. The gate carries the mail, why it matters and which of its three choices you suggest. It appears on the gates page and in the assigned member’s chat thread, where the CEO chooses Reply, Snooze or Leave it. Reply and Snooze end this run with outcome ceo_approved, the choice as ceoChoice (reply or snooze), this mail as mailId, and ceoNote: what the reply should say, or the ISO 8601 time the snooze ends. Leave it cancels this run and archives the mail. Call it last.',
        inputSchema: {
            type: 'object',
            properties: {
                id: { type: 'string', description: 'The mail the escalation is about' },
                mailId: { type: 'string', description: 'The same id, under the key both mail gates use' },
                subject: { type: 'string' },
                fromAddress: { type: 'string' },
                summary: { type: 'string', description: 'Why this mail needs the CEO' },
                suggestion: {
                    type: 'string',
                    description:
                        'Which choice you suggest (Reply, Snooze or Leave it) and why, in one or two sentences',
                },
            },
            required: ['id', 'subject', 'fromAddress', 'summary', 'suggestion'],
            additionalProperties: false,
        },
        returns:
            'The CEO’s choice, to the task that runs next: `ceoChoice` (`reply` or `snooze`), `ceoNote` and `mailId`. Leave it hands nothing on: the run is cancelled and the mail archived.',
        callers: ['run', 'os'],
        effect: 'write',
        sideEffects: 'Opens one CEO gate for this run and parks the run until it is decided.',
        retry: 'unsafe',
        retryNote:
            'Escalating the same mail again opens a second gate carrying its own summary, so the CEO is asked twice. Whether a mail has already been escalated is triage’s to track, not this gate’s.',
        needs: { gate: 'always' },
        // The card leads with the mail itself, in the OS's words: the escalation carries both fields, always.
        gateCard: { title: 'Reply to “{subject}”', why: 'Escalated mail from {fromAddress}' },
        gateChoices: [
            {
                key: 'reply',
                label: 'Reply',
                hint: 'A reply is drafted and comes back to you to approve before anything is sent.',
                decision: 'approve',
                input: { kind: 'text', label: 'What should the reply say? (optional)' },
            },
            {
                key: 'snooze',
                label: 'Snooze',
                hint: 'Nothing is sent. The mail is set aside until the time you pick.',
                decision: 'approve',
                input: { kind: 'datetime', label: 'Snooze until', required: true },
            },
            {
                key: 'leave',
                label: 'Leave it',
                hint: 'No reply is sent and the mail is archived.',
                decision: 'reject',
                input: { kind: 'text', label: 'Reason (optional)' },
            },
        ],
        errors: [],
        examples: [
            {
                input: {
                    id: '5f0c1d2e-0000-4000-8000-000000000003',
                    subject: 'Renewal before Friday',
                    fromAddress: 'vip@acme.example',
                    summary: 'A VIP contact wants to renew before Friday and has asked for a call.',
                    suggestion: 'Reply: confirm the renewal terms and offer Thursday for the call.',
                },
            },
        ],
        public: true,
    },
    {
        name: 'os.mail_reply_draft',
        family: 'os.escalation',
        summary: 'The gate a drafted reply opens, so no reply leaves without a decision on it.',
        description:
            'Nobody calls this: the OS opens this gate itself when the CEO’s instruction on an escalated mail is to reply. It carries the mail the reply answers and the drafted body, and waits for a decision before anything is sent.',
        inputSchema: {
            type: 'object',
            properties: {
                mailId: { type: 'string', description: 'The mail this reply answers' },
                body: { type: 'string', description: 'The drafted reply, as it would be sent' },
            },
            required: ['mailId', 'body'],
            additionalProperties: false,
        },
        returns: "The decision on the draft: approved and it may be sent, or not, with the decider's note.",
        callers: ['os'],
        effect: 'write',
        retry: 'unsafe',
        retryNote:
            'A second draft for the same mail opens a second gate, decided on its own: nothing folds it into the first.',
        errors: [],
        examples: [
            {
                input: {
                    mailId: '5f0c1d2e-0000-4000-8000-000000000003',
                    body: 'Thursday works — I will send an invitation shortly.',
                },
            },
        ],
        public: true,
    },
]
